import React, { useState, useEffect, useCallback } from 'react';
import { getApiErrorMessage, getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/ui/Layout';
import ActionDropdown from '../../components/ui/ActionDropdown';
import { cn } from '../../utils/cn';
import { getRentalProductLabel } from '../../utils/rentalProductLabel';
import { CanCreate, CanEdit, CanDelete, PermissionGuard } from '../../components/auth/PermissionGuard';
import { usePermission } from '../../hooks/usePermission';
import {
  RiAddLine,
  RiSearchLine,
  RiFilterLine,
  RiFileDownloadLine,
  RiFileUploadLine,
  RiEyeLine,
  RiEditLine,
  RiDeleteBinLine,
  RiRefreshLine,
  RiTruckLine,
  RiCalendarCheckLine,
  RiFileTextLine,
  RiTimeLine,
  RiAlertLine,
  RiCloseLine,
  RiCalendarLine as RiCalendarAddLine,
  RiCloseCircleLine,
  RiErrorWarningLine,
  RiFileListLine,
  RiSettings3Line,
  RiStickyNoteLine,
  RiCheckLine,
  RiPrinterLine,
  // RiMoneyDollarCircleLine, // Removed - was used for Payment action
} from 'react-icons/ri';
import rentalService, { Rental, RentalStatus, RentalType, GetRentalsParams } from '../../services/rentalService';
import { showAlert, showConfirm, showSuccess, showError, reportedError } from '../../utils/alert';
import { formatDate } from '../../utils/dateUtils';
import { daysUntilDue } from '../../utils/rentalExpiry';
// formatDateTime removed - created_at/updated_at columns hidden from table
import RentalHistoryModal, { RentalHistoryEntry } from '../../components/rental/RentalHistoryModal';
import BulkStatusUpdateModal from '../../components/rental/BulkStatusUpdateModal';
import RentalNotesModal from '../../components/rental/RentalNotesModal';
import CancelGoodsCheckModal from '../../components/rental/CancelGoodsCheckModal';
import RentalExtensionModal from '../../components/rental/RentalExtensionModal';
import RentalExtensionHistoryComponent from '../../components/rental/RentalExtensionHistory';
import RentalCancellationFlowModal from '../../components/rental/RentalCancellationFlowModal';
import ContractEndReturnModal from '../../components/rental/ContractEndReturnModal';
import DeletedRentalsModal from '../../components/rental/DeletedRentalsModal';
import Modal from '../../components/ui/Modal';
// Invoice/Payment modals removed - will be replaced with Company-level system
// import PaymentListModal from '../../components/rental/PaymentListModal';
// import InvoiceListModal from '../../components/rental/InvoiceListModal';
import AdvancedSearchModal, { SearchField } from '../../components/search/AdvancedSearchModal';
import useAdvancedSearch from '../../hooks/useAdvancedSearch';
import { queryKeys } from '../../queryClient';
import { useCreateRental, useUpdateRental, useDeleteRental, useUpdateRentalStatus, useApproveRental } from '../../hooks/mutations/useRentalMutations';
import { getRentalActions } from '../../utils/rentalActions';
import Pagination from '../../components/ui/Pagination';
import DualScrollWrapper from '../../components/ui/DualScrollWrapper';
import { PAGINATION_CONFIG } from '../../config/pagination';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { RentalStatusBadge } from '../../components/ui/StatusBadge';
import { useClampPage } from '../../hooks/useClampPage';

// สถานะการค้นหาและกรอง
interface FilterState {
  search: string;
  status?: RentalStatus;
  rental_type?: RentalType;
  company_id?: number;
  site_id?: number;
  product_id?: number;
  from_date?: string;
  to_date?: string;
  is_expiring_soon?: boolean;
  days_to_expiry?: number;
  // เพิ่มสำหรับการค้นหาขั้นสูง
  company_name?: string;
  site_name?: string;
  product_name?: string;
  device_code?: string;
  is_software_only?: string; // 'true' | 'false' | undefined (ทั้งหมด)
}

/**
 * แปลง FilterState ของหน้าจอ → query params ของ API — **แหล่งเดียว**
 * ใช้ทั้งการโหลดรายการและการส่งออก Excel
 *
 * GAP-EXPORT-FILTER: เดิมแต่ละที่ไล่พิมพ์ params เองซ้ำกัน แล้วฝั่ง export ตกตัวกรอง
 * ค้นหาขั้นสูง 4 ตัว (company_name / site_name / product_name / device_code)
 * ⇒ ผู้ใช้กรองด้วยชื่อบริษัท/ชื่อโครงการแล้วกดส่งออก **ได้ไฟล์ที่ไม่ตรงกับที่เห็นบนจอ**
 * โดยไม่มี error ให้เห็น · รวมไว้ที่เดียวแล้วดริฟต์อีกไม่ได้เชิงโครงสร้าง
 *
 * หมายเหตุ: **ไม่ส่ง `product_id`** — มีอยู่ใน FilterState แต่ไม่มี UI ตัวไหนตั้งค่าเลย
 * (dead plumbing) และ backend ทั้ง list/export ก็ไม่รับ ⇒ ส่งไปก็ถูกเมินเงียบ ๆ
 */
export const buildRentalFilterParams = (filters: FilterState): GetRentalsParams => ({
  search: filters.search || undefined,
  status: filters.status,
  rental_type: filters.rental_type,
  company_id: filters.company_id,
  site_id: filters.site_id,
  start_date: filters.from_date,
  end_date: filters.to_date,
  is_expiring_soon: filters.is_expiring_soon,
  days_to_expiry: filters.days_to_expiry,
  // ค้นหาขั้นสูง — ใส่เฉพาะที่มีค่า เพื่อไม่ให้ส่งคีย์ว่างขึ้นไป
  ...(filters.company_name && { company_name: filters.company_name }),
  ...(filters.site_name && { site_name: filters.site_name }),
  ...(filters.product_name && { product_name: filters.product_name }),
  ...(filters.device_code && { device_code: filters.device_code }),
  ...(filters.is_software_only !== undefined && {
    is_software_only: filters.is_software_only === 'true',
  }),
});

/** ช่วง "ใกล้หมดอายุ" ที่หน้านี้เสนอ (ปุ่มลัด 7/30 + dropdown 7/15/30/60) — ค่าอื่นจาก URL ไม่รับ */
const EXPIRING_DAY_OPTIONS = [7, 15, 30, 60];

const RentalPage: React.FC = () => {
  const navigate = useNavigate();
  const { canView, canEdit, canDelete, hasPermission, canExport } = usePermission();

  // BUG-181 FIX: Two-way URL filter sync using useSearchParams
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  // OBS-S1104-001 FIX: read `page` param from URL for refresh-state-restore
  const pageParam = searchParams.get('page');

  // State สำหรับ pagination และ search
  const [showFilters, setShowFilters] = useState<boolean>(!!statusParam);
  const [filters, setFilters] = useState<FilterState>(() => {
    // KD3 S2482 (REGR-MOVE-008): คำค้นอยู่ใน URL (?q=) → ดูรายละเอียดแล้วกด Back คำค้น+ผลลัพธ์ไม่หาย
    const base: FilterState = { search: searchParams.get('q') || '' };
    if (statusParam === 'expiring') {
      // BUG-S2074-001 (คู่กับหน้าสัญญา): เดิมคืน 30 ตายตัว → ปุ่ม 7 วันหายเมื่อรีเฟรช/แชร์ลิงก์
      const urlDays = Number(searchParams.get('days'));
      const days = EXPIRING_DAY_OPTIONS.includes(urlDays) ? urlDays : 30;
      return { ...base, status: RentalStatus.ACTIVE, is_expiring_soon: true, days_to_expiry: days };
    }
    if (statusParam === 'active') return { ...base, status: RentalStatus.ACTIVE };
    if (statusParam === 'pending_cancellation') return { ...base, status: RentalStatus.PENDING_CANCELLATION };
    if (statusParam === 'expired' || statusParam === 'completed') return { ...base, status: RentalStatus.COMPLETED };
    if (statusParam === 'cancelled') return { ...base, status: RentalStatus.CANCELLED };
    if (statusParam === 'draft') return { ...base, status: RentalStatus.DRAFT };
    return base;
  });

  // OBS-S1104-001 FIX: Sync filter + page state back to URL
  // page=1 ไม่ใส่ใน URL (clean URL) · page>1 ใส่ใน URL · invalid → fail-open to page 1
  const syncFiltersToUrl = useCallback((currentFilters: FilterState, page: number) => {
    const newParams = new URLSearchParams();
    if (currentFilters.is_expiring_soon) {
      newParams.set('status', 'expiring');
      if (currentFilters.days_to_expiry && currentFilters.days_to_expiry !== 30) {
        newParams.set('days', String(currentFilters.days_to_expiry));
      }
    } else if (currentFilters.status) {
      const statusMap: Record<string, string> = {
        [RentalStatus.ACTIVE]: 'active',
        [RentalStatus.PENDING_CANCELLATION]: 'pending_cancellation',
        [RentalStatus.COMPLETED]: 'completed',
        [RentalStatus.CANCELLED]: 'cancelled',
        [RentalStatus.DRAFT]: 'draft',
      };
      const urlStatus = statusMap[currentFilters.status];
      if (urlStatus) newParams.set('status', urlStatus);
    }
    if (currentFilters.search) newParams.set('q', currentFilters.search);
    // OBS-S1104-001: only add page param when > 1 (clean URL when on first page)
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  // OBS-S1104-001 FIX: read `page` from URL on init + defensive parse + fail-open to 1
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!pageParam) return 1;
    const n = parseInt(pageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1; // fail-open: invalid/negative → page 1
    return Math.min(n, 9999); // upper bound guard (won't trust >9999 from URL)
  });

  useEffect(() => {
    syncFiltersToUrl(filters, currentPage);
  }, [filters, currentPage, syncFiltersToUrl]);

  // Save last list URL (path + query) so view/edit pages can navigate back
  // to the exact page/filter user was on, instead of resetting to page 1.
  useEffect(() => {
    const url = window.location.pathname + window.location.search;
    try { sessionStorage.setItem('krp:lastRentalListUrl', url); } catch {}
  }, [searchParams]);

  // OBS-S1107-001 FIX: reset currentPage to 1 when filter content (JSON shape) actually changes
  // - JSON-ref pattern handles ALL filter onChange uniformly (combobox/select/date range/etc.)
  // - Mount-safe: initial JSON matches ref → no spurious setCurrentPage(1) on mount
  // - Only fires on real user filter changes (not on every re-render with same filter object ref)
  const filtersJsonRef = React.useRef(JSON.stringify(filters));
  useEffect(() => {
    const newJson = JSON.stringify(filters);
    if (newJson !== filtersJsonRef.current) {
      filtersJsonRef.current = newJson;
      setCurrentPage(1);
    }
  }, [filters]);

  const [itemsPerPage, setItemsPerPage] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  useUserPageSize(itemsPerPage, setItemsPerPage);
  // Mutation hooks
  const createRentalMutation = useCreateRental();
  const updateRentalMutation = useUpdateRental();
  const deleteRentalMutation = useDeleteRental();
  const updateStatusMutation = useUpdateRentalStatus();
  const approveMutation = useApproveRental();

  // State สำหรับ bulk operations, history และ notes
  const [selectedRentals, setSelectedRentals] = useState<number[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState<boolean>(false);
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [showExtensionModal, setShowExtensionModal] = useState<boolean>(false);
  const [showExtensionHistoryModal, setShowExtensionHistoryModal] = useState<boolean>(false);
  const [selectedRentalForHistory, setSelectedRentalForHistory] = useState<Rental | null>(null);
  const [selectedRentalForNotes, setSelectedRentalForNotes] = useState<Rental | null>(null);
  const [selectedRentalForCancelCheck, setSelectedRentalForCancelCheck] = useState<Rental | null>(null);
  const [selectedRentalForExtension, setSelectedRentalForExtension] = useState<Rental | null>(null);
  const [selectedRentalForExtensionHistory, setSelectedRentalForExtensionHistory] = useState<Rental | null>(null);
  const [showCancellationFlowModal, setShowCancellationFlowModal] = useState<boolean>(false);
  const [selectedRentalForCancellation, setSelectedRentalForCancellation] = useState<Rental | null>(null);
  const [showContractEndReturnModal, setShowContractEndReturnModal] = useState<boolean>(false);
  const [showDeletedRentalsModal, setShowDeletedRentalsModal] = useState<boolean>(false);
  const [selectedRentalForContractEndReturn, setSelectedRentalForContractEndReturn] = useState<Rental | null>(null);
  const [rentalHistory, setRentalHistory] = useState<RentalHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  // Invoice/Payment modal states removed - will be replaced with Company-level system
  // const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  // const [showInvoiceModal, setShowInvoiceModal] = useState<boolean>(false);
  // const [selectedRentalId, setSelectedRentalId] = useState<number | null>(null);
  // const [selectedRentalCode, setSelectedRentalCode] = useState<string>('');
  // const [selectedRentalMonthlyFee, setSelectedRentalMonthlyFee] = useState<number | undefined>(undefined);

  // Clear selection when filters or pagination change
  useEffect(() => {
    setSelectedRentals([]);
  }, [currentPage, itemsPerPage, filters]);

  // React Query สำหรับดึงข้อมูล
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...queryKeys.rentals.list(), { 
      page: currentPage,
      limit: itemsPerPage,
      ...filters
    }],
    queryFn: async () => {
      return await rentalService.getRentals({
        page: currentPage,
        limit: itemsPerPage,
        ...buildRentalFilterParams(filters),
      });
    },
  });
  
  // ข้อมูลจาก React Query
  const rentals = data?.items || [];
  const totalItems = data?.total || 0;
  useClampPage(currentPage, data?.total, itemsPerPage, setCurrentPage, isLoading);
  
  // Loading state จาก mutations
  const loading = isLoading ||
                 createRentalMutation.isPending ||
                 updateRentalMutation.isPending ||
                 deleteRentalMutation.isPending;

  // DRAFT count — lightweight background query (limit:1 fetches metadata only, not full rows)
  // Used to surface the "ร่างรออนุมัติ" quick-stat badge without blocking the main list.
  const { data: draftCountData } = useQuery({
    queryKey: [...queryKeys.rentals.list(), 'draft-count'],
    queryFn: () => rentalService.getRentals({ status: RentalStatus.DRAFT, limit: 1, page: 1 }),
    staleTime: 60_000, // 1 min — count doesn't need real-time refresh
    select: (res) => res.total,
  });
  const draftCount = draftCountData ?? 0;

  // Advanced search configuration
  const searchFields: SearchField[] = [
    {
      key: 'code',
      label: 'รหัสการเช่า',
      type: 'text',
      placeholder: 'ค้นหาตามรหัสการเช่า'
    },
    {
      key: 'company_name',
      label: 'ชื่อบริษัท',
      type: 'text',
      placeholder: 'ค้นหาตามชื่อบริษัท'
    },
    {
      key: 'site_name',
      label: 'ชื่อไซต์',
      type: 'text',
      placeholder: 'ค้นหาตามชื่อไซต์'
    },
    {
      key: 'product_name',
      label: 'ชื่อสินค้า',
      type: 'text',
      placeholder: 'ค้นหาตามชื่อสินค้า'
    },
    {
      key: 'device_code',
      label: 'รหัสอุปกรณ์',
      type: 'text',
      placeholder: 'ค้นหาตามรหัสอุปกรณ์'
    },
    {
      key: 'status',
      label: 'สถานะการเช่า',
      type: 'select',
      options: [
        { value: RentalStatus.ACTIVE, label: 'เช่าใช้อยู่' },
        { value: RentalStatus.CANCELLED, label: 'ยกเลิกแล้ว' },
        { value: RentalStatus.PENDING_CANCELLATION, label: 'รอการยกเลิก' },
        { value: RentalStatus.COMPLETED, label: 'เสร็จสิ้น/คืนแล้ว' },
        { value: RentalStatus.DRAFT, label: 'ร่าง/รอดำเนินการ' }
      ]
    },
    {
      key: 'rental_type',
      label: 'ประเภทการเช่า',
      type: 'select',
      options: [
        { value: 'rent', label: 'เช่า' },
        { value: 'purchase', label: 'ซื้อ' },
        { value: 'trial', label: 'ทดลองใช้' }
      ]
    },
    {
      key: 'rental_date',
      label: 'วันที่เริ่มเช่า',
      type: 'dateRange'
    }
  ];

  // Advanced search hook
  const advancedSearch = useAdvancedSearch({
    searchFields,
    onSearch: (searchFilters) => {
      
      // Log filters for debugging
      // Create new filters object, only including values that exist
      const newFilters: FilterState = {
        search: searchFilters.code || ''
      };
      
      // Add only defined values
      if (searchFilters.status) newFilters.status = searchFilters.status as RentalStatus;
      if (searchFilters.rental_type) newFilters.rental_type = searchFilters.rental_type as RentalType;
      if (searchFilters.rental_date?.from) newFilters.from_date = searchFilters.rental_date.from;
      if (searchFilters.rental_date?.to) newFilters.to_date = searchFilters.rental_date.to;
      if (searchFilters.company_name) newFilters.company_name = searchFilters.company_name;
      if (searchFilters.site_name) newFilters.site_name = searchFilters.site_name;
      if (searchFilters.product_name) newFilters.product_name = searchFilters.product_name;
      if (searchFilters.device_code) newFilters.device_code = searchFilters.device_code;
      
      setFilters(newFilters);
      setCurrentPage(1);
    },
    storageKey: 'rental_search_filters'
  });
  
  // ฟังก์ชันสำหรับการค้นหา
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // รีเซ็ตหน้าเมื่อค้นหาใหม่
  };

  // ฟังก์ชันสำหรับล้างตัวกรอง
  const handleClearFilters = () => {
    setFilters({ search: '' });
    setCurrentPage(1);
    advancedSearch.handleClearFilters();
  };

  // ฟังก์ชันสำหรับกรองรายการใกล้หมดอายุ
  const handleFilterExpiringRentals = (days: number = 30) => {
    setFilters({
      ...filters,
      status: RentalStatus.ACTIVE, // เฉพาะรายการที่ยังใช้งานอยู่
      is_expiring_soon: true,
      days_to_expiry: days
    });
    setCurrentPage(1);
  };

  // ฟังก์ชันสำหรับการลบรายการเช่า
  const handleDeleteRental = async (id: number) => {
    showConfirm(
      'ยืนยันการลบรายการเช่า',
      'คุณต้องการลบรายการเช่านี้ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนแปลงได้',
      async () => {
        deleteRentalMutation.mutate(id, {
          onSuccess: () => {
            // notification จะแสดงผ่าน mutation โดยอัตโนมัติ
          },
          // KD3 S2123 (BUG-S2123-003): ไม่ต้อง showAlert ซ้ำ — hook (useMutationWithRefresh) แสดงข้อความจริงจาก backend แล้ว
        });
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // ฟังก์ชันสำหรับการเปลี่ยนหน้า
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // ฟังก์ชันสำหรับการเลือกรายการเช่าทั้งหมด
  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedRentals(rentals.map(rental => rental.id));
    } else {
      setSelectedRentals([]);
    }
  };

  // ฟังก์ชันสำหรับการเลือกรายการเช่าแต่ละรายการ
  const handleSelectRental = (rentalId: number, selected: boolean) => {
    if (selected) {
      setSelectedRentals(prev => [...prev, rentalId]);
    } else {
      setSelectedRentals(prev => prev.filter(id => id !== rentalId));
    }
  };

  // FB-2026-0017 พิมพ์ใบส่งของ — PDF ไฟล์เดียว 1 ใบต่อ (บริษัท + โครงการ) · ไม่มีราคา
  // ด่านสถานะ (ร่าง/ยกเลิก = 400) อยู่ที่ backend ตัวเดียว · ข้อความ error มาเป็น Blob → อ่านด้วย Async
  const [printingDeliveryNotes, setPrintingDeliveryNotes] = useState(false);
  const handlePrintDeliveryNotes = async () => {
    if (selectedRentals.length === 0 || printingDeliveryNotes) return;
    if (selectedRentals.length > 100) {
      showError('พิมพ์ใบส่งของได้ครั้งละไม่เกิน 100 รายการ');
      return;
    }
    setPrintingDeliveryNotes(true);
    try {
      await rentalService.downloadDeliveryNotes(selectedRentals);
      showSuccess('สร้างใบส่งของสำเร็จ');
    } catch (error) {
      console.error('Error printing delivery notes:', error);
      showError(await getApiErrorMessageAsync(error, 'ไม่สามารถสร้างใบส่งของได้ กรุณาลองใหม่อีกครั้ง'));
    } finally {
      setPrintingDeliveryNotes(false);
    }
  };

  // ฟังก์ชันสำหรับดูประวัติการเช่า
  const handleExportExcel = async () => {
    try {
      // ใช้ตัวสร้าง params ตัวเดียวกับหน้ารายการ — ไฟล์ที่ได้ต้องเป็นชุดเดียวกับที่เห็นบนจอ
      // เดิมไล่พิมพ์ซ้ำที่นี่แล้วตกตัวกรองค้นหาขั้นสูง 4 ตัว (ชื่อบริษัท/ชื่อโครงการ/
      // ชื่อสินค้า/รหัสอุปกรณ์) ⇒ ผู้ใช้กรองแล้วได้ไฟล์ที่ไม่ตรงกับจอ โดยไม่มีอะไรเตือน
      await rentalService.exportToExcel(buildRentalFilterParams(filters));
      
      showSuccess('ดาวน์โหลดรายการเช่าสำเร็จ');
    } catch (error) {
      console.error('Error exporting rentals:', error);
      showError(await getApiErrorMessageAsync(error, 'ไม่สามารถดาวน์โหลดรายการเช่าได้'));
    }
  };

  const handleViewHistory = async (rental: Rental) => {
    setSelectedRentalForHistory(rental);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    
    try {
      // เรียก API จริงเพื่อดึงประวัติการเช่า
      const historyData = await rentalService.getRentalHistory(rental.id);
      
      // แปลงข้อมูลจาก API ให้ตรงกับ interface ของ Component
      const formattedHistory: RentalHistoryEntry[] = historyData.map((entry: any) => ({
        id: entry.id,
        action: entry.action,
        description: entry.description,
        oldValue: entry.old_value,
        newValue: entry.new_value,
        amount: entry.amount,
        performedBy: entry.performed_by || entry.user_name || 'ระบบ',
        performedAt: entry.performed_at || entry.created_at,
        notes: entry.notes,
        attachments: entry.attachments || []
      }));
      
      setRentalHistory(formattedHistory);
    } catch (error) {
      console.error('Error fetching rental history:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดประวัติการเช่าได้', 'error');
      // ใส่ array ว่างเมื่อเกิดข้อผิดพลาด
      setRentalHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ฟังก์ชันสำหรับอัพเดตสถานะหลายรายการ
  const handleBulkStatusUpdate = async (newStatus: string, reason: string, effectiveDate: string) => {
    try {
      // BUG-167 FIX: ใช้ API จริงแทน mock — เรียก individual status update ทีละ rental
      // C7 (MANUAL_AUDIT_SYSTEM_FINDINGS): ส่ง "เหตุผล" + "วันที่มีผล" ที่โมดัลบังคับกรอกไปด้วย
      // เดิมรับ 2 ค่านี้เข้ามาแล้วโยนทิ้ง ผู้ใช้พิมพ์เหตุผลไปก็ไม่มีที่ไหนเก็บ
      const results = await Promise.allSettled(
        selectedRentals.map(id =>
          rentalService.updateRentalStatus(id, newStatus, reason, effectiveDate)
        )
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (failed > 0) {
        showAlert('บางส่วนสำเร็จ', `อัพเดตสำเร็จ ${succeeded} รายการ, ล้มเหลว ${failed} รายการ`, 'warning');
      } else {
        showAlert('สำ���ร็จ', `อัพเดตสถานะ ${succeeded} รายการเป็น ${newStatus} เรี��บร้อยแ��้ว`, 'success');
      }
      
      // รีเซ็ตการเลือกและรีเฟรชข้อมูล
      setSelectedRentals([]);
      setShowBulkStatusModal(false);
      refetch();
    } catch (error) {
      console.error('Error updating rental statuses:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถอัพเดตสถานะได้', 'error');
    }
  };

  // ฟังก์ชันสำหรับดูหมายเหตุ
  const handleViewNotes = (rental: Rental) => {
    setSelectedRentalForNotes(rental);
    setShowNotesModal(true);
  };

  // ฟังก์ชันสำหรับอนุมัติร่างการเช่า → เริ่มสัญญา (DRAFT → ACTIVE)
  const handleApproveRental = (rental: Rental) => {
    showConfirm(
      'อนุมัติการเช่า',
      `อนุมัติและเริ่มสัญญาเช่า ${rental.code || rental.rental_number} ใช่หรือไม่? อุปกรณ์จะถูกจองและสถานะเปลี่ยนเป็น "เช่าใช้อยู่"`,
      async () => {
        try {
          await approveMutation.mutateAsync({ id: rental.id });
          refetch();
        } catch (error: any) {
          const msg = error?.response?.data?.error?.user_message || error?.response?.data?.detail || 'ไม่สามารถอนุมัติได้';
          refetch(); // KD3 S2357: อีกคนอนุมัติ/ยกเลิกไปก่อน → แถวยังโชว์ "ร่าง" + ปุ่มอนุมัติ ขัดกับข้อความ
          // toast ถูกแสดงโดย useMutationWithRefresh แล้ว → throw แบบ reported กัน toast ซ้ำ
          throw reportedError(msg);
        }
      },
      { confirmText: 'อนุมัติ', cancelText: 'ยกเลิก' }
    );
  };

  // ฟังก์ชันสำหรับยกเลิกร่างการเช่า (DRAFT → CANCELLED)
  // OBS-W2-A: เดิมข้อความบอกว่า "ไม่กระทบอุปกรณ์" ซึ่งไม่จริงตั้งแต่มีใบงานเตรียมเครื่อง —
  // ร่างที่มาจาก convert ยังมีของที่ใบงานจองไว้ (RESERVED) และ crud.cancel() ปล่อยคืนคลังให้
  // ผ่าน release_for_rental() → ของกลับเป็น AVAILABLE จริง
  const handleVoidDraft = (rental: Rental) => {
    showConfirm(
      'ยกเลิกร่าง',
      `ยกเลิกร่างการเช่า ${rental.code || rental.rental_number}? · ร่างยังไม่ได้จองอุปกรณ์เอง แต่ถ้าร่างนี้มาจากใบงานเตรียมเครื่อง อุปกรณ์ที่ใบงานกันไว้จะถูกปล่อยกลับคลังทันที`,
      async () => {
        try {
          await updateStatusMutation.mutateAsync({ id: rental.id, status: RentalStatus.CANCELLED });
          refetch();
        } catch (error: any) {
          const msg = error?.response?.data?.error?.user_message || error?.response?.data?.detail || 'ไม่สามารถยกเลิกได้';
          refetch(); // KD3 S2357: สถานะอาจถูกเปลี่ยนจากที่อื่นแล้ว
          throw reportedError(msg);
        }
      },
      { isDanger: true, confirmText: 'ยกเลิกร่าง', cancelText: 'ปิด' }
    );
  };

  // ฟังก์ชันสำหรับยกเลิกการเช่า
  // L1 (2026-09-08): ยกเลิกไม่ปล่อยของกลับคลังเองแล้ว — ต้องถามก่อนว่าของกลับหรือยัง
  // (เดิม: ยกเลิกทันที แล้วค่อยเด้ง ReturnOptionModal ชวนไปสร้างใบตามเก็บที่หน้าอื่น ซึ่ง prod
  //  พิสูจน์แล้วว่าไม่มีใครทำต่อ — ใบตามเก็บ 43 ใบ ไม่เคยถูกปิดเลยสักใบ)
  const handleCancelRental = (rental: Rental) => {
    setSelectedRentalForCancelCheck(rental);
  };

  const handleConfirmCancelWithGoods = async (
    rental: Rental,
    answer: {
      equipment_returned: boolean;
      equipment_condition?: 'GOOD' | 'DAMAGED' | 'LOST';
      delivery_date?: string;
    }
  ) => {
    const { delivery_date, ...statusAnswer } = answer;
    // บันทึกวันส่งมอบก่อนยกเลิก — เกณฑ์เดียวที่ backend ใช้ตัดสินว่าออกใบตามเก็บหรือปล่อยของ
    // (BUG-UI-08) · payload มีคีย์เดียว ไม่มี rental_items จึงไม่แตะรายการอุปกรณ์
    if (delivery_date) {
      await rentalService.updateRental(rental.id, { delivery_date });
    }
    await rentalService.updateRentalStatus(
      rental.id, RentalStatus.CANCELLED, undefined, undefined, statusAnswer
    );
    refetch();
    showAlert(
      'สำเร็จ',
      answer.equipment_returned
        ? 'ยกเลิกการเช่าแล้ว · อุปกรณ์กลับเข้าคลังเรียบร้อย'
        : 'ยกเลิกการเช่าแล้ว · ระบบเปิดใบตามเก็บให้แล้ว บันทึกรับคืนได้ที่หน้ารายละเอียดการเช่า',
      'success'
    );
  };


  // ฟังก์ชันสำหรับต่อการเช่า
  const handleExtendRental = (rental: Rental) => {
    setSelectedRentalForExtension(rental);
    setShowExtensionModal(true);
  };

  // ฟังก์ชันสำหรับดูประวัติการต่ออายุ
  const handleViewExtensionHistory = (rental: Rental) => {
    setSelectedRentalForExtensionHistory(rental);
    setShowExtensionHistoryModal(true);
  };

  // ฟังก์ชันสำหรับเปิด Cancellation Flow Modal
  const handleOpenCancellationFlow = (rental: Rental) => {
    setSelectedRentalForCancellation(rental);
    setShowCancellationFlowModal(true);
  };

  // ฟังก์ชันสำหรับปิด Cancellation Flow Modal
  const handleCancellationFlowClose = () => {
    setShowCancellationFlowModal(false);
    setSelectedRentalForCancellation(null);
  };

  // ฟังก์ชันตรวจสอบว่าควรแสดง menu "ขอคืนอุปกรณ์" หรือไม่
  const shouldShowContractEndReturn = (rental: Rental): boolean => {
    if (rental.status !== RentalStatus.ACTIVE) return false;
    const endDate = rental.end_date || rental.expected_return_date;
    if (!endDate) return false;
    const today = new Date();
    const expiryDate = new Date(endDate);
    const daysLeft = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysLeft <= 30; // แสดงเมื่อใกล้หมด 30 วัน หรือหมดแล้ว
  };

  // ฟังก์ชันสำหรับเปิด Contract End Return Modal
  const handleRequestContractEndReturn = (rental: Rental) => {
    setSelectedRentalForContractEndReturn(rental);
    setShowContractEndReturnModal(true);
  };

  // Invoice/Payment modal handlers removed - will be replaced with Company-level system
  // const handleOpenPaymentModal = (rental: Rental) => {
  //   setSelectedRentalId(rental.id);
  //   setSelectedRentalCode(rental.code);
  //   setShowPaymentModal(true);
  // };

  // const handleOpenInvoiceModal = (rental: Rental) => {
  //   setSelectedRentalId(rental.id);
  //   setSelectedRentalCode(rental.code);
  //   setSelectedRentalMonthlyFee(rental.monthly_fee);
  //   setShowInvoiceModal(true);
  // };

  // formatDate: ใช้ central utility จาก dateUtils.ts (BUG-S1043-001/002 fix S1045 + migrate S1046)
  // - honors user `displaySettings.dateFormat` ผ่าน module-level cache populated โดย AuthContext
  // - fallback เป็น 'th-TH' short locale เมื่อ user ไม่ได้ตั้งค่า (compat กับ behavior เดิม)
  // - ลบ local function เดิมที่ hardcode 'th-TH' + 'Asia/Bangkok' ออก เพราะ ignore user preference

  // ฟังก์ชันสำหรับแสดงประเภทการเช่า
  const renderRentalTypeBadge = (type: RentalType): JSX.Element => {
    switch (type) {
      case RentalType.RENT:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200/50 shadow-sm shadow-primary-100">
            เช่า
          </span>
        );
      case RentalType.PURCHASE:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-accent-50 to-accent-100 text-accent-700 border border-accent-200/50 shadow-sm shadow-accent-100">
            ซื้อ
          </span>
        );
      case RentalType.TRIAL:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-warning-50 to-warning-100 text-warning-700 border border-warning-200/50 shadow-sm shadow-warning-100">
            ทดลองใช้
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-secondary-50 to-secondary-100 text-secondary-600 border border-secondary-200/50 shadow-sm shadow-secondary-100">
            {type}
          </span>
        );
    }
  };

  // ฟังก์ชันสำหรับคำนวณและแสดงวันที่หมดอายุ
  const calculateDaysToExpiry = (endDate: string | undefined): { days: number; isExpiringSoon: boolean; isExpired: boolean } => {
    if (!endDate) return { days: 0, isExpiringSoon: false, isExpired: false };
    
    // BUG-S2338-001: นับเป็นวันปฏิทิน (daysUntilDue) ให้ตรง backend is_expiring_soon —
    // เดิมเทียบเวลา → ครบกำหนด "วันนี้" ได้ 0 แล้วขึ้น "หมดอายุแล้ว" ทั้งที่ตัวกรอง "ใกล้หมดอายุ" ยังนับอยู่
    const diffDays = daysUntilDue(endDate);
    if (diffDays === null) return { days: 0, isExpiringSoon: false, isExpired: false };

    return {
      days: diffDays,
      isExpiringSoon: diffDays <= 30 && diffDays >= 0,
      isExpired: diffDays < 0
    };
  };

  // ฟังก์ชันสำหรับแสดงสถานะการหมดอายุ
  const renderExpiryStatus = (rental: Rental): JSX.Element | null => {
    const endDate = rental.end_date || rental.expected_return_date;
    
    const { days, isExpiringSoon, isExpired } = calculateDaysToExpiry(endDate);
    
    if (rental.status !== RentalStatus.ACTIVE) return null;
    
    // แสดงจำนวนครั้งที่ต่อ
    const extensionInfo = rental.extension_count && rental.extension_count > 0 ? (
      <span className="ml-1 text-xs text-secondary-500">
        (ต่อแล้ว {rental.extension_count} ครั้ง)
      </span>
    ) : null;

    if (isExpired) {
      return (
        <div className="flex items-center">
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-danger-100 text-danger-800">
            <RiCloseCircleLine className="w-3 h-3 mr-1" />
            หมดอายุแล้ว
          </span>
          {extensionInfo}
        </div>
      );
    }

    if (isExpiringSoon) {
      const bgColor = days <= 7 ? 'bg-danger-100 text-danger-800' : days <= 15 ? 'bg-warning-100 text-warning-800' : 'bg-warning-100 text-warning-800';
      return (
        <div className="flex items-center">
          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${bgColor}`}>
            <RiTimeLine className="w-3 h-3 mr-1" />
            {days === 0 ? 'ครบกำหนดวันนี้' : `เหลือ ${days} วัน`}
          </span>
          {extensionInfo}
        </div>
      );
    }

    // แสดงสถานะปกติสำหรับการเช่าที่ยังไม่ใกล้หมดอายุ
    if (endDate && days > 30) {
      return (
        <div className="flex items-center">
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-800">
            <RiCalendarCheckLine className="w-3 h-3 mr-1" />
            ปกติ ({days} วัน)
          </span>
          {extensionInfo}
        </div>
      );
    }

    return !endDate ? (
      <span className="text-xs text-secondary-500">
        -
      </span>
    ) : null;
  };

  return (
    <Layout>
      <div className="w-full">
        {/* Page Header - Clean & Consistent */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <RiTruckLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-secondary-900 leading-tight">
                รายการเช่า
                {filters.is_expiring_soon && (
                  <span className="ml-2 text-sm font-normal text-warning-600">
                    (ใกล้หมดอายุใน {filters.days_to_expiry} วัน)
                  </span>
                )}
              </h1>
              <p className="text-xs text-secondary-500 mt-0.5">
                {filters.is_expiring_soon
                  ? `แสดงรายการเช่าที่ใกล้หมดอายุภายใน ${filters.days_to_expiry} วัน`
                  : 'จัดการข้อมูลการเช่าทั้งหมด'
                }
              </p>
            </div>
          </div>
          {totalItems > 0 && (
            <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
              {totalItems.toLocaleString()} รายการ
            </span>
          )}
        </div>

        {/* Action Buttons Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          {/* Quick Filter Buttons */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-secondary-600 font-medium hidden sm:inline">ดูด่วน:</span>
            <button
              type="button"
              onClick={() => handleFilterExpiringRentals(7)}
              className={cn(
                'inline-flex items-center px-3.5 min-h-[44px] sm:min-h-[38px] border rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-warning-500 focus:ring-offset-2',
                filters.is_expiring_soon && filters.days_to_expiry === 7
                  ? 'border-warning-400 text-warning-700 bg-warning-100 ring-2 ring-warning-400/40 shadow-sm'
                  : 'border-warning-300 text-warning-700 bg-warning-50 hover:bg-warning-100 hover:border-warning-400'
              )}
            >
              <RiTimeLine className="mr-1.5 h-4 w-4" />
              7 วัน
            </button>
            <button
              type="button"
              onClick={() => handleFilterExpiringRentals(30)}
              className={cn(
                'inline-flex items-center px-3.5 min-h-[44px] sm:min-h-[38px] border rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-warning-500 focus:ring-offset-2',
                filters.is_expiring_soon && filters.days_to_expiry === 30
                  ? 'border-warning-400 text-warning-700 bg-warning-100 ring-2 ring-warning-400/40 shadow-sm'
                  : 'border-warning-300 text-warning-700 bg-warning-50 hover:bg-warning-100 hover:border-warning-400'
              )}
            >
              <RiCalendarCheckLine className="mr-1.5 h-4 w-4" />
              30 วัน
            </button>
            {draftCount > 0 && (
              <button
                type="button"
                onClick={() => setFilters({ search: '', status: RentalStatus.DRAFT })}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3.5 min-h-[44px] sm:min-h-[38px] border rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-warning-500 focus:ring-offset-2',
                  filters.status === RentalStatus.DRAFT && !filters.is_expiring_soon
                    ? 'border-warning-400 text-warning-800 bg-warning-100 ring-2 ring-warning-400/40 shadow-sm'
                    : 'border-warning-300 text-warning-800 bg-warning-50 hover:bg-warning-100 hover:border-warning-400'
                )}
              >
                <RiStickyNoteLine className="h-4 w-4 flex-shrink-0" />
                <span>ร่างรออนุมัติ</span>
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-warning-200 text-warning-800 text-xs font-bold leading-none">
                  {draftCount}
                </span>
              </button>
            )}
          </div>

          {/* Right side buttons */}
          <div className="flex items-center gap-2">
            <CanCreate module="rentals">
              <button
                type="button"
                onClick={() => navigate('/rentals/create')}
                className="group inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg shadow-button hover:bg-primary-700 hover:shadow-button-hover focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
              >
                <RiAddLine className="mr-1 h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                สร้างรายการเช่าใหม่
              </button>
            </CanCreate>

            {canExport('rentals') && (
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={isLoading}
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-300 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-50 hover:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RiFileDownloadLine className="mr-1.5 h-4 w-4" />
                Export Excel
              </button>
            )}

            {/* รายการที่ลบ + กู้คืน — backend gate GET deleted/list ด้วย rentals.delete */}
            <PermissionGuard requires="rentals.delete">
              <button
                type="button"
                onClick={() => setShowDeletedRentalsModal(true)}
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-300 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-50 hover:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
              >
                <RiDeleteBinLine className="mr-1.5 h-4 w-4" />
                รายการที่ลบ
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Alert for expiring rentals - show only when not already filtering by expiry */}
        {!filters.is_expiring_soon && rentals.length > 0 && (() => {
          const expiringRentals = rentals.filter(rental => {
            if (rental.status !== RentalStatus.ACTIVE) return false;
            const endDate = rental.end_date || rental.expected_return_date;
            const { isExpiringSoon } = calculateDaysToExpiry(endDate);
            return isExpiringSoon;
          });
          
          if (expiringRentals.length > 0) {
            return (
              <div className="mb-5 bg-warning-50 border border-warning-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-warning-100 rounded-lg flex items-center justify-center">
                    <RiAlertLine className="h-4 w-4 text-warning-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-semibold text-warning-800">
                      พบการเช่าที่ใกล้หมดอายุ {expiringRentals.length} รายการในหน้านี้
                    </h2>
                    <div className="mt-1.5 text-xs text-warning-700 space-y-0.5">
                      {expiringRentals.slice(0, 3).map(rental => {
                        const endDate = rental.end_date || rental.expected_return_date;
                        const { days } = calculateDaysToExpiry(endDate);
                        return (
                          <div key={rental.id} className="flex items-center gap-1.5">
                            <span className="w-1 h-1 bg-warning-400 rounded-full flex-shrink-0"></span>
                            <span className="font-medium">{rental.code}</span>
                            <span className="text-warning-700">-</span>
                            <span className="truncate">{rental.company_name}</span>
                            <span className="text-warning-700 font-medium flex-shrink-0">({days === 0 ? 'ครบกำหนดวันนี้' : `เหลือ ${days} วัน`})</span>
                          </div>
                        );
                      })}
                      {expiringRentals.length > 3 && (
                        <div className="text-warning-700">และอีก {expiringRentals.length - 3} รายการ...</div>
                      )}
                    </div>
                    <div className="mt-2.5">
                      <button
                        type="button"
                        onClick={() => handleFilterExpiringRentals(30)}
                        className="text-xs font-semibold text-warning-700 hover:text-warning-900 underline underline-offset-2 transition-colors"
                      >
                        กรองดูทั้งหมด
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}
        
        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm px-4 py-3 mb-4">
          {/* Section header with icon box */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 bg-primary-100 rounded-md flex items-center justify-center">
              <RiSearchLine className="w-3.5 h-3.5 text-primary-600" />
            </div>
            <h2 className="text-sm font-semibold text-secondary-700">ค้นหาและกรองข้อมูล</h2>
          </div>

          <div className="mb-3">
            <form onSubmit={handleSearch} noValidate className="flex flex-wrap items-center gap-2">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <RiSearchLine className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  type="text"
                  className="input pl-10 pr-12 text-sm"
                  placeholder="ค้นหารายการเช่า..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowFilters(!showFilters)}
                    aria-label={showFilters ? 'ซ่อนตัวกรอง' : 'แสดงตัวกรอง'}
                    aria-pressed={showFilters}
                    className={cn(
                      'inline-flex items-center justify-center p-2.5 rounded-lg transition-colors min-w-[44px] min-h-[44px]',
                      showFilters
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-secondary-600 hover:text-secondary-700 hover:bg-secondary-100'
                    )}
                  >
                    <RiFilterLine className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <button
                type="submit"
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 border border-transparent rounded-lg shadow-button text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 hover:shadow-button-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                ค้นหา
              </button>
              <button
                type="button"
                onClick={advancedSearch.openSearchModal}
                className={cn(
                  'inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 border text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200',
                  advancedSearch.hasActiveFilters
                    ? 'border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100'
                    : 'border-secondary-300 text-secondary-700 bg-white hover:bg-secondary-50 hover:border-secondary-400'
                )}
              >
                <RiFilterLine className="-ml-1 mr-2 h-4 w-4" />
                ค้นหาขั้นสูง
                {advancedSearch.hasActiveFilters && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-primary-200 text-primary-800 rounded-full font-semibold">
                    {advancedSearch.getActiveFilterCount()}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  handleClearFilters();
                  setSelectedRentals([]);
                  refetch();
                }}
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-300 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-50 hover:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
              >
                ล้างตัวกรอง
              </button>
              <button
                type="button"
                onClick={() => refetch()}
                className="group inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-300 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-50 hover:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
              >
                <RiRefreshLine className="mr-1.5 h-4 w-4 group-hover:rotate-180 transition-transform duration-500" />
                รีเฟรช
              </button>
            </form>
          </div>

          {/* Bulk Actions */}
          {selectedRentals.length > 0 && (
            <div className="mb-4 p-3.5 bg-info-50 border border-info-200 rounded-xl">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-info-100 rounded-md flex items-center justify-center">
                    <RiFileListLine className="w-3.5 h-3.5 text-info-600" />
                  </div>
                  <span className="text-sm font-semibold text-info-900">
                    เลือกแล้ว {selectedRentals.length} รายการ
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {hasPermission('rentals.edit') && (
                    <button
                      type="button"
                      onClick={() => setShowBulkStatusModal(true)}
                      className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 border border-transparent text-xs font-medium rounded-lg text-info-700 bg-info-100 hover:bg-info-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 transition-colors"
                    >
                      อัพเดตสถานะ
                    </button>
                  )}
                  {/* FB-2026-0017: สิทธิ์เดียวกับ backend (rentals.view) — งานอ่าน ไม่ใช่ rentals.edit */}
                  {hasPermission('rentals.view') && (
                    <button
                      type="button"
                      onClick={handlePrintDeliveryNotes}
                      disabled={printingDeliveryNotes}
                      data-testid="bulk-print-delivery-note"
                      title="PDF ไฟล์เดียว 1 ใบต่อบริษัท + โครงการ (ไม่มีราคา)"
                      className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 border border-transparent text-xs font-medium rounded-lg text-info-700 bg-info-100 hover:bg-info-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 transition-colors disabled:opacity-60 disabled:cursor-wait"
                    >
                      <RiPrinterLine className="mr-1 h-3.5 w-3.5" />
                      {printingDeliveryNotes ? 'กำลังสร้างใบส่งของ…' : 'พิมพ์ใบส่งของ'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedRentals([])}
                    className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 border border-secondary-300 text-xs font-medium rounded-lg text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 transition-colors"
                  >
                    ยกเลิกการเลือก
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {showFilters && (
            <div className="mb-4 bg-secondary-50 border border-secondary-200 p-4 rounded-xl">
              <h2 className="text-sm font-semibold text-secondary-700 mb-3">ตัวกรองเพิ่มเติม</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-secondary-700 mb-1">
                    สถานะการเช่า
                  </label>
                  <select
                    id="status"
                    className="select text-sm"
                    value={filters.status ?? ''}
                    onChange={(e) => setFilters({ ...filters, status: (e.target.value as RentalStatus) || undefined })}
                  >
                    <option value="">ทั้งหมด</option>
                    <option value={RentalStatus.ACTIVE}>เช่าใช้อยู่</option>
                    <option value={RentalStatus.PENDING_CANCELLATION}>รอการยกเลิก</option>
                    <option value={RentalStatus.CANCELLED}>ยกเลิกแล้ว</option>
                    <option value={RentalStatus.COMPLETED}>เสร็จสิ้น</option>
                    <option value={RentalStatus.DRAFT}>ร่าง</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="expiring-filter" className="block text-sm font-medium text-secondary-700 mb-1">
                    รายการใกล้หมดอายุ
                  </label>
                  <select
                    id="expiring-filter"
                    className="select text-sm"
                    value={filters.is_expiring_soon ? (filters.days_to_expiry || 30).toString() : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setFilters({
                          ...filters,
                          is_expiring_soon: undefined,
                          days_to_expiry: undefined
                        });
                      } else {
                        const days = parseInt(value);
                        setFilters({
                          ...filters,
                          is_expiring_soon: true,
                          days_to_expiry: days,
                          status: RentalStatus.ACTIVE // เฉพาะรายการที่ใช้งานอยู่
                        });
                      }
                    }}
                  >
                    <option value="">ทั้งหมด</option>
                    <option value="7">หมดอายุใน 7 วัน</option>
                    <option value="15">หมดอายุใน 15 วัน</option>
                    <option value="30">หมดอายุใน 30 วัน</option>
                    <option value="60">หมดอายุใน 60 วัน</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="rental-type" className="block text-sm font-medium text-secondary-700 mb-1">
                    ประเภทการเช่า
                  </label>
                  <select
                    id="rental-type"
                    className="select text-sm"
                    value={filters.rental_type ?? ''}
                    onChange={(e) => setFilters({ ...filters, rental_type: (e.target.value as RentalType) || undefined })}
                  >
                    <option value="">ทั้งหมด</option>
                    <option value={RentalType.RENT}>เช่า</option>
                    <option value={RentalType.PURCHASE}>ซื้อ</option>
                    <option value={RentalType.TRIAL}>ทดลองใช้</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="product-type" className="block text-sm font-medium text-secondary-700 mb-1">
                    รูปแบบสินค้า
                  </label>
                  <select
                    id="product-type"
                    className="select text-sm"
                    value={filters.is_software_only || ''}
                    onChange={(e) => setFilters({ ...filters, is_software_only: e.target.value || undefined })}
                  >
                    <option value="">ทั้งหมด</option>
                    <option value="true">Software อย่างเดียว</option>
                    <option value="false">Hardware (มีอุปกรณ์)</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="from-date" className="block text-sm font-medium text-secondary-700 mb-1">
                    ตั้งแต่วันที่
                  </label>
                  <input
                    type="date"
                    id="from-date"
                    className="input text-sm"
                    value={filters.from_date || ''}
                    onChange={(e) => setFilters({ ...filters, from_date: e.target.value || undefined })}
                  />
                </div>
                <div>
                  <label htmlFor="to-date" className="block text-sm font-medium text-secondary-700 mb-1">
                    ถึงวันที่
                  </label>
                  <input
                    type="date"
                    id="to-date"
                    className="input text-sm"
                    value={filters.to_date || ''}
                    onChange={(e) => setFilters({ ...filters, to_date: e.target.value || undefined })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {advancedSearch.hasActiveFilters && (
            <div className="mb-4 p-3.5 bg-info-50 border border-info-200 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs font-semibold text-info-900">ตัวกรองขั้นสูงที่ใช้งานอยู่</h2>
                <button
                  onClick={advancedSearch.handleClearFilters}
                  className="text-xs font-medium text-info-600 hover:text-info-800 underline underline-offset-2 transition-colors"
                >
                  ล้างทั้งหมด
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {advancedSearch.getFilterSummary().map((filter, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-info-100 text-info-800 rounded-full border border-info-200"
                  >
                    {filter}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {error && (
            <div className="mb-4 p-4 border border-danger-200 rounded-xl bg-danger-50">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-danger-100 rounded-lg flex items-center justify-center">
                  <RiErrorWarningLine className="h-4 w-4 text-danger-600" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-danger-800">เกิดข้อผิดพลาด</h2>
                  <p className="mt-0.5 text-sm text-danger-700">
                    {getApiErrorMessage(error, 'ไม่สามารถโหลดข้อมูลได้')}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {loading || isLoading ? (
            <DualScrollWrapper className="border border-secondary-200 rounded-xl overflow-hidden">
              <table className="min-w-full divide-y divide-secondary-100" aria-label="รายการเช่า">
                <thead className="bg-secondary-50 border-b-2 border-secondary-200 sticky top-0 z-10">
                  <tr>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">รหัสการเช่า</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">ประเภท</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">บริษัท/โครงการ</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">จำนวนรายการ</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">วันที่เช่า</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">วันสิ้นสุดเก็บเงิน</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">ค่าเช่ารายเดือน</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">สถานะ</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">การหมดอายุ</th>
                    <th scope="col" className="sticky right-0 z-10 bg-secondary-50 px-3 py-3 text-center text-xs font-semibold text-secondary-600 whitespace-nowrap shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.06)]">การดำเนินการ</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-100">
                  {Array.from({ length: Math.min(itemsPerPage, 5) }).map((_, index) => (
                    <tr key={`skeleton-${index}`} className="animate-pulse">
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <div className="h-3.5 bg-secondary-200 rounded mb-1 w-24"></div>
                        <div className="h-3 bg-secondary-100 rounded w-20"></div>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <div className="h-5 bg-secondary-200 rounded-full w-12"></div>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <div className="h-3.5 bg-secondary-200 rounded mb-1 w-28"></div>
                        <div className="h-3 bg-secondary-100 rounded mb-1 w-20"></div>
                        {/* บรรทัดชื่อผลิตภัณฑ์ — ให้ความสูงแถวตอนโหลดเท่ากับตอนแสดงจริง */}
                        <div className="h-3 bg-secondary-100 rounded w-16"></div>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <div className="h-3.5 bg-secondary-200 rounded w-16"></div>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <div className="h-3.5 bg-secondary-200 rounded w-14"></div>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <div className="h-3.5 bg-secondary-200 rounded w-14"></div>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <div className="h-3.5 bg-secondary-200 rounded w-20"></div>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <div className="h-5 bg-secondary-200 rounded-full w-16"></div>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <div className="h-5 bg-secondary-200 rounded-full w-20"></div>
                      </td>
                      <td className="sticky right-0 z-10 bg-white px-3 py-3.5 whitespace-nowrap">
                        <div className="flex justify-end">
                          <div className="h-7 w-7 bg-secondary-200 rounded-lg"></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DualScrollWrapper>
          ) : error ? null /* BUG-S2273-001: โหลดล้ม → แบนเนอร์ error พอ ไม่ขึ้น "ไม่พบ…ตรงตามเงื่อนไข" + ปุ่มสร้างซ้อน */ : rentals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-secondary-50 ring-1 ring-secondary-200 flex items-center justify-center">
                <RiFileListLine className="w-7 h-7 text-secondary-400" />
              </div>
              <div className="text-center">
                <h2 className="text-sm font-semibold text-secondary-800">ไม่พบรายการเช่า</h2>
                <p className="mt-1 text-sm text-secondary-500 max-w-xs">ไม่พบรายการเช่าที่ตรงตามเงื่อนไขการค้นหา ลองปรับตัวกรองใหม่</p>
              </div>
              <CanCreate module="rentals">
                <Link
                  to="/rentals/create"
                  className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-sm transition-all duration-200"
                >
                  <RiAddLine className="mr-1.5 h-4 w-4" />
                  สร้างรายการเช่าใหม่
                </Link>
              </CanCreate>
            </div>
          ) : (
            <DualScrollWrapper className="border border-secondary-200 rounded-xl overflow-hidden">
              <table className="min-w-full divide-y divide-secondary-100" aria-label="รายการเช่า">
                <thead className="bg-secondary-50 border-b-2 border-secondary-200 sticky top-0 z-10">
                  <tr>
                    <th scope="col" className="px-3 py-3 text-center text-xs font-semibold text-secondary-500 w-10">
                      <input
                        type="checkbox"
                        checked={rentals.length > 0 && selectedRentals.length === rentals.length}
                        ref={(input) => { if (input) { input.indeterminate = selectedRentals.length > 0 && selectedRentals.length < rentals.length; } }}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                        aria-label="เลือกทั้งหมด"
                      />
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-500 whitespace-nowrap">รหัสการเช่า</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-500 whitespace-nowrap">ประเภท</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-500 whitespace-nowrap">บริษัท/โครงการ</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-500 whitespace-nowrap">จำนวนรายการ</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-500 whitespace-nowrap">วันที่เช่า</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-500 whitespace-nowrap">วันสิ้นสุดเก็บเงิน</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-500 whitespace-nowrap">ค่าเช่ารายเดือน</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-500 whitespace-nowrap">สถานะ</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-500 whitespace-nowrap">การหมดอายุ</th>
                    <th scope="col" className="sticky right-0 z-10 bg-secondary-50 px-3 py-3 text-center text-xs font-semibold text-secondary-500 whitespace-nowrap shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.06)]">
                      การดำเนินการ
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-100">
                  {rentals.map((rental) => {
                    const a = getRentalActions(rental, { hasPermission, canView, canEdit, canDelete });
                    return (
                    <tr
                      key={rental.id}
                      tabIndex={0}
                      role="row"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLButtonElement)) {
                          navigate(`/rentals/view/${rental.id}`);
                        }
                      }}
                      className={cn(
                        'group transition-colors duration-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500',
                        selectedRentals.includes(rental.id)
                          ? 'bg-primary-50 hover:bg-primary-100/60'
                          : 'hover:bg-secondary-50/70'
                      )}
                    >
                      <td className="px-3 py-3.5 whitespace-nowrap text-center">
                        <input
                          type="checkbox"
                          checked={selectedRentals.includes(rental.id)}
                          onChange={(e) => handleSelectRental(rental.id, e.target.checked)}
                          className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                          aria-label={`เลือก ${rental.code}`}
                        />
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-semibold text-primary-700">{rental.code}</span>
                        {rental.contract_number && (
                          <div className="text-xs text-secondary-600 mt-0.5">
                            สัญญา: {rental.contract_number}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        {renderRentalTypeBadge(rental.rental_type)}
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <div className="text-sm font-medium text-secondary-800">{rental.company_name || <span className="text-secondary-300">—</span>}</div>
                        <div className="text-xs text-secondary-600 mt-0.5">{rental.site_name || ''}</div>
                        {/* ชื่อผลิตภัณฑ์ — feedback amintra 4 ก.ย. 2026 (ข้อมูลมากับ API รายการอยู่แล้ว ไม่ยิงเพิ่ม) */}
                        {(() => {
                          const product = getRentalProductLabel(rental);
                          return product ? (
                            <div className="text-xs text-primary-700 mt-0.5" title={product.title}>
                              {product.text}
                            </div>
                          ) : null;
                        })()}
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-secondary-700">{rental.items_count || 1} รายการ</span>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap text-sm text-secondary-600">
                        {formatDate(rental.rental_date)}
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap text-sm text-secondary-600">
                        {rental.billing_end_date
                          ? formatDate(rental.billing_end_date)
                          : <span className="text-emerald-700 font-medium text-xs">ยังเช่าอยู่ (Ongoing)</span>}
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap text-sm text-secondary-700 tabular-nums font-medium">
                        {rental.monthly_fee ? `฿${rental.monthly_fee.toLocaleString()}` : <span className="text-secondary-300 font-normal">—</span>}
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <RentalStatusBadge status={rental.status as any} size="sm" bordered />
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        {renderExpiryStatus(rental)}
                      </td>
                      <td className={cn(
                        'sticky right-0 z-10 px-3 py-3.5 whitespace-nowrap text-sm text-secondary-500 transition-colors duration-100 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.06)]',
                        selectedRentals.includes(rental.id) ? 'bg-primary-50 group-hover:bg-primary-100/60' : 'bg-white group-hover:bg-secondary-50/70'
                      )}>
                        <ActionDropdown
                          actions={[
                            // === กลุ่ม: ดูข้อมูล (ต้องมี rentals.view) ===
                            ...(a.view ? [
                              {
                                groupHeader: true,
                                label: 'ดูข้อมูล',
                                icon: <RiFileListLine className="w-3.5 h-3.5" />,
                                onClick: () => {},
                              },
                              {
                                label: 'ดูรายละเอียด',
                                icon: <RiEyeLine className="w-4 h-4" />,
                                onClick: () => navigate(`/rentals/view/${rental.id}`)
                              },
                              ...(a.viewContract ? [{
                                label: 'ดูสัญญา',
                                icon: <RiFileTextLine className="w-4 h-4" />,
                                onClick: () => navigate(`/contracts/view/${rental.contract_id}`)
                              }] : []),
                              {
                                label: 'ดูหมายเหตุ',
                                icon: <RiStickyNoteLine className="w-4 h-4" />,
                                onClick: () => handleViewNotes(rental)
                              },
                              {
                                label: 'ดูประวัติการเปลี่ยนแปลง',
                                icon: <RiTimeLine className="w-4 h-4" />,
                                onClick: () => handleViewHistory(rental)
                              },
                              ...(a.viewExtensionHistory ? [{
                                label: 'ดูประวัติการต่อเช่า',
                                icon: <RiCalendarCheckLine className="w-4 h-4" />,
                                onClick: () => handleViewExtensionHistory(rental),
                                className: 'text-info-600 hover:text-info-700 hover:bg-info-50'
                              }] : []),
                            ] : []),

                            // === กลุ่ม: เริ่มใช้งาน (อนุมัติ DRAFT → ACTIVE) ===
                            ...(a.approve ? [
                              { divider: true, label: '', onClick: () => {} },
                              {
                                groupHeader: true,
                                label: 'เริ่มใช้งาน',
                                icon: <RiCheckLine className="w-3.5 h-3.5" />,
                                onClick: () => {},
                              },
                              {
                                label: 'อนุมัติ → เริ่มเช่า',
                                icon: <RiCheckLine className="w-4 h-4" />,
                                onClick: () => handleApproveRental(rental),
                                variant: 'success' as const,
                                className: 'text-success-600 hover:text-success-700 hover:bg-success-50'
                              },
                            ] : []),

                            // === กลุ่ม: จัดการ (แก้ไข / ต่ออายุ / ขอคืน) ===
                            ...((a.edit || a.extend) ? [
                              { divider: true, label: '', onClick: () => {} },
                              {
                                groupHeader: true,
                                label: 'จัดการ',
                                icon: <RiSettings3Line className="w-3.5 h-3.5" />,
                                onClick: () => {},
                              },
                              ...(a.edit ? [{
                                label: 'แก้ไขข้อมูล',
                                icon: <RiEditLine className="w-4 h-4" />,
                                onClick: () => navigate(`/rentals/edit/${rental.id}`)
                              }] : []),
                              ...(a.extend ? [{
                                label: 'ต่ออายุการเช่า',
                                icon: <RiCalendarAddLine className="w-4 h-4" />,
                                onClick: () => handleExtendRental(rental),
                                className: 'text-primary-600 hover:text-primary-700 hover:bg-primary-50'
                              }] : []),
                            ] : []),

                            // === กลุ่ม: การคืนอุปกรณ์ (Sprint 3 — รวม "ขอคืน" เป็นระบบเดียว) ===
                            // ทุกหน้า (List + Detail) ใช้เส้นทางเดียวกัน: ไปหน้า equipment-returns hub
                            // (hub รองรับทั้งคืนทั่วไป + สิ้นสุดสัญญาผ่าน /from-contract-end) แสดงตาม matrix
                            // requestReturn = ACTIVE/PENDING/CANCELLED ที่ยังไม่ได้คืนของจริง
                            ...(a.requestReturn ? [
                              { divider: true, label: '', onClick: () => {} },
                              { groupHeader: true, label: 'การคืนอุปกรณ์', icon: <RiTruckLine className="w-3.5 h-3.5" />, onClick: () => {} },
                              {
                                label: 'ขอคืนอุปกรณ์',
                                icon: <RiTruckLine className="w-4 h-4" />,
                                onClick: () => navigate(`/rentals/equipment-returns?rentalId=${rental.id}`),
                                className: 'text-accent-600 hover:text-accent-700 hover:bg-accent-50'
                              }
                            ] : []),

                            // === กลุ่ม: ยุติการเช่า (ต้องมี rentals.cancel) ===
                            ...(a.cancel ? [
                              { divider: true, label: '', onClick: () => {} },
                              {
                                groupHeader: true,
                                label: 'ยุติการเช่า',
                                icon: <RiCloseLine className="w-3.5 h-3.5" />,
                                onClick: () => {},
                              },
                              {
                                label: a.cancelMode === 'void' ? 'ยกเลิกร่าง' : a.cancelMode === 'continue' ? 'ดำเนินการยกเลิก (ต่อ)' : 'ดำเนินการยกเลิก...',
                                icon: <RiAlertLine className="w-4 h-4" />,
                                onClick: () => {
                                  if (a.cancelMode === 'void') handleVoidDraft(rental);
                                  else if (a.cancelMode === 'continue') navigate(`/rentals/cancellation-tracking?rentalId=${rental.id}`);
                                  else handleOpenCancellationFlow(rental);
                                },
                                variant: 'warning' as const,
                                className: 'text-warning-600 hover:text-warning-700 hover:bg-warning-50'
                              }
                            ] : []),

                            // === กลุ่ม: ลบข้อมูล (ต้องมี rentals.delete และ CANCELLED/COMPLETED) ===
                            ...(a.delete ? [
                              { divider: true, label: '', onClick: () => {} },
                              {
                                groupHeader: true,
                                label: 'ลบข้อมูล',
                                icon: <RiDeleteBinLine className="w-3.5 h-3.5" />,
                                onClick: () => {},
                              },
                              {
                                label: 'ลบรายการเช่า',
                                icon: <RiDeleteBinLine className="w-4 h-4" />,
                                onClick: () => handleDeleteRental(rental.id),
                                className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
                              }
                            ] : [])
                          ]}
                          buttonIcon={null}
                          buttonLabel=""
                        />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </DualScrollWrapper>
          )}

          {/* Pagination */}
          {!loading && !isLoading && rentals.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              pageSize={itemsPerPage}
              onPageChange={handlePageChange}
              onPageSizeChange={(newPageSize) => {
                setItemsPerPage(newPageSize);
                setCurrentPage(1);
              }}
              showPageSizeSelector={true}
              showSummary={true}
              className="mt-5"
            />
          )}
        </div>

        {/* Rental History Modal */}
        <RentalHistoryModal
          isOpen={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedRentalForHistory(null);
            setRentalHistory([]);
          }}
          rentalId={selectedRentalForHistory?.id || 0}
          rentalCode={selectedRentalForHistory?.code || ''}
          history={rentalHistory}
          loading={historyLoading}
        />

        {/* Bulk Status Update Modal */}
        <BulkStatusUpdateModal
          isOpen={showBulkStatusModal}
          onClose={() => setShowBulkStatusModal(false)}
          selectedRentals={selectedRentals.map(id => {
            const rental = rentals.find(r => r.id === id);
            return {
              id,
              code: rental?.code || '',
              customerName: rental?.customer_name || '',
              currentStatus: rental?.status || ''
            };
          })}
          onUpdate={handleBulkStatusUpdate}
          loading={loading}
        />

        {/* Advanced Search Modal */}
        <AdvancedSearchModal
          isOpen={advancedSearch.isSearchModalOpen}
          onClose={advancedSearch.closeSearchModal}
          onSearch={advancedSearch.handleSearch}
          onSaveFilter={advancedSearch.handleSaveFilter}
          onDeleteFilter={advancedSearch.handleDeleteFilter}
          searchFields={searchFields}
          savedFilters={advancedSearch.savedFilters}
          currentFilters={advancedSearch.currentFilters}
          title="ค้นหาการเช่าขั้นสูง"
          loading={loading}
        />

        {/* Rental Notes Modal */}
        <RentalNotesModal
          isOpen={showNotesModal}
          onClose={() => {
            setShowNotesModal(false);
            setSelectedRentalForNotes(null);
          }}
          rentalId={selectedRentalForNotes?.id || 0}
          rentalCode={selectedRentalForNotes?.code || ''}
        />

        {/* L1: ถาม "ของกลับแล้วหรือยัง" ก่อนยกเลิก — แทน ReturnOptionModal เดิมที่เด้งขึ้น
            "หลัง" ยกเลิกเพื่อชวนไปสร้างใบตามเก็บที่หน้าอื่น (ไม่มีใครทำต่อ) ตอนนี้ระบบ
            เปิดใบให้เองเมื่อของยังไม่กลับ และปิดใบให้เองเมื่อผู้ใช้บอกว่าของกลับครบ */}
        {selectedRentalForCancelCheck && (
          <CancelGoodsCheckModal
            isOpen={!!selectedRentalForCancelCheck}
            onClose={() => setSelectedRentalForCancelCheck(null)}
            rentalNumber={selectedRentalForCancelCheck.code || selectedRentalForCancelCheck.rental_number || ''}
            outstandingCount={0}
            deliveryDate={selectedRentalForCancelCheck.delivery_date}
            canEditRental={hasPermission('rentals.edit')}
            onConfirm={async (answer) => {
              const target = selectedRentalForCancelCheck;
              try {
                await handleConfirmCancelWithGoods(target, answer);
              } catch (error: any) {
                const serverMessage = error?.response?.data?.error?.user_message
                  || error?.response?.data?.error?.message
                  || error?.response?.data?.detail
                  || 'ไม่สามารถยกเลิกการเช่าได้';
                showAlert('เกิดข้อผิดพลาด', serverMessage, 'error');
                throw reportedError(serverMessage);
              }
            }}
          />
        )}

        {/* Rental Extension Modal */}
        <RentalExtensionModal
          isOpen={showExtensionModal}
          onClose={() => {
            setShowExtensionModal(false);
            setSelectedRentalForExtension(null);
          }}
          rentalId={selectedRentalForExtension?.id || 0}
          rentalCode={selectedRentalForExtension?.code || ''}
          currentEndDate={selectedRentalForExtension?.expected_return_date || selectedRentalForExtension?.end_date || ''}
          currentRentalFee={selectedRentalForExtension?.monthly_fee}
          onSuccess={() => {
            refetch();
          }}
        />

        {/* Rental Extension History Modal */}
        <Modal
          isOpen={showExtensionHistoryModal}
          onClose={() => {
            setShowExtensionHistoryModal(false);
            setSelectedRentalForExtensionHistory(null);
          }}
          title="ประวัติการต่ออายุ"
          size="xl"
        >
          {selectedRentalForExtensionHistory && (
            <RentalExtensionHistoryComponent
              rentalId={selectedRentalForExtensionHistory.id}
              rentalCode={selectedRentalForExtensionHistory.code || selectedRentalForExtensionHistory.rental_number || ''}
            />
          )}
        </Modal>

        {/* Rental Cancellation Flow Modal */}
        <RentalCancellationFlowModal
          isOpen={showCancellationFlowModal}
          onClose={handleCancellationFlowClose}
          rental={selectedRentalForCancellation}
          onSelectNotification={() => {
            handleCancellationFlowClose();
            if (selectedRentalForCancellation) {
              navigate(`/rentals/cancellation-tracking?rentalId=${selectedRentalForCancellation.id}`);
            }
          }}
          onSelectImmediate={() => {
            handleCancellationFlowClose();
            if (selectedRentalForCancellation) {
              handleCancelRental(selectedRentalForCancellation);
            }
          }}
        />

        {/* Contract End Return Modal */}
        {selectedRentalForContractEndReturn && (
          <ContractEndReturnModal
            isOpen={showContractEndReturnModal}
            onClose={() => {
              setShowContractEndReturnModal(false);
              setSelectedRentalForContractEndReturn(null);
            }}
            rental={selectedRentalForContractEndReturn}
            onSuccess={() => {
              refetch();
              showAlert('สำเร็จ', 'สร้างรายการขอคืนอุปกรณ์แล้ว', 'success');
            }}
          />
        )}

        {/* Deleted Rentals Modal — จอ "รายการที่ลบ" + กู้คืน (soft delete restore) */}
        <DeletedRentalsModal
          isOpen={showDeletedRentalsModal}
          onClose={() => setShowDeletedRentalsModal(false)}
          onRestored={() => refetch()}
        />

        {/* Invoice/Payment modals removed - will be replaced with Company-level system */}
        {/*
        <PaymentListModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedRentalId(null);
            setSelectedRentalCode('');
          }}
          rentalId={selectedRentalId || 0}
          rentalCode={selectedRentalCode}
        />

        <InvoiceListModal
          isOpen={showInvoiceModal}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedRentalId(null);
            setSelectedRentalCode('');
            setSelectedRentalMonthlyFee(undefined);
          }}
          rentalId={selectedRentalId || 0}
          rentalCode={selectedRentalCode}
          monthlyFee={selectedRentalMonthlyFee}
        />
        */}
      </div>
    </Layout>
  );
};

export default RentalPage;